import * as lunarCalendar from 'korean-lunar-calendar';
import type { Schedule, TimelineBlock } from './types.js';

// CJS/ESM 겸용 패키지 — esModuleInterop을 켜지 않고 기본 내보내기를 꺼내 쓴다.
const KoreanLunarCalendar = (lunarCalendar as unknown as { default: new () => {
  setSolarDate(year: number, month: number, day: number): boolean;
  setLunarDate(year: number, month: number, day: number, intercalation: boolean): boolean;
  getLunarCalendar(): { year: number; month: number; day: number; intercalation?: boolean };
  getSolarCalendar(): { year: number; month: number; day: number };
} }).default;

export type AssignmentLayer = 'base' | 'event';

/**
 * 애플 캘린더와 같은 의미다.
 *  · 기간(startDate–endDate)은 "1회분"의 길이다. 같으면 하루, 다르면 연속된 며칠.
 *  · 반복은 그 1회분을 통째로 되풀이한다. 월~금을 1회분으로 잡고 매주 반복하면 매주 평일이다.
 *  · 반복 종료는 횟수(첫 회 포함) 또는 마지막으로 시작할 수 있는 날짜로 끊는다.
 * 24시간 템플릿을 붙이는 구조라 '매일' 반복은 두지 않는다 — 기간을 늘리면 같은 뜻이다.
 */
export type AssignmentRepeat =
  | { kind: 'none' }
  | { kind: 'weekly'; weekdays: number[] }
  | { kind: 'monthly' }
  | { kind: 'monthly-weekday' }
  | { kind: 'yearly' };

/**
 * 날짜를 고르고 해석하는 기준. 저장은 언제나 양력 ISO 날짜지만,
 * 음력을 고르면 매월·매년 반복이 음력 날짜를 따라간다(설날·추석).
 * 주 단위는 두 달력이 같으므로 영향을 받지 않는다.
 */
export type AssignmentCalendar = 'solar' | 'lunar';

export type AssignmentRepeatEnd =
  | { kind: 'never' }
  | { kind: 'count'; count: number }
  | { kind: 'until'; date: string };

export type AssignmentRule = {
  /** 생략하면 양력. */
  calendar?: AssignmentCalendar;
  startDate: string;
  endDate: string;
  repeat: AssignmentRepeat;
  repeatEnd: AssignmentRepeatEnd;
  /** 이 편성에서 건너뛸 날짜(YYYY-MM-DD). "이 날짜만 삭제"가 여기에 쌓인다. */
  skipDates?: string[];
};

export type ScheduleAssignment = AssignmentRule & {
  id: string;
  /** 같은 요청으로 여러 기기에 붙인 편성을 묶는 값. 일괄 수정·삭제에 쓴다. */
  batchId: string;
  deviceId: string;
  groupId: string | null;
  layer: AssignmentLayer;
  templateId: string;
  name: string;
  blocks: TimelineBlock[];
  /** 최고관리자가 올린 편성. 예전 "통합송출"이 이 자리로 들어왔다. */
  central?: boolean;
  /** 승인이 필요한 편성(또는 수정·삭제 제안)에만 붙는다. */
  approval?: AssignmentApproval;
  /** 올린 사람. 상위 권한자가 올린 편성은 하위 권한자가 고치거나 지울 수 없다(보기만). */
  createdBy?: string | null;
  createdByRole?: AssignmentRole;
  /**
   * 손으로 올리거나 내린 편성의 재생 순서. 등록 시각 대신 이 값으로 줄을 세운다.
   * "방금 등록한 것으로 친다"는 뜻이라, 이후에 새로 등록된 편성은 다시 위로 올라간다.
   */
  priorityAt?: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
};

// ── 승인 ───────────────────────────────────────────────────────────────────
// 그룹의 승인 방식(중앙/그룹/자동)과 올린 사람의 권한에 따라 승인을 받아야 적용된다.
// 승인 전에는 캘린더에 "승인 대기"로 남고 재생되지 않는다. 이미 재생 중인 편성을 고치거나 지우는
// 요청은 원본을 그대로 둔 채 복제본(제안)으로 대기하고, 승인되면 원본을 대체한다.
export type AssignmentApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type AssignmentAction = 'create' | 'update' | 'delete';
export type AssignmentRole = 'Owner' | 'Admin' | 'Editor' | 'Viewer';
export type AssignmentApproval = {
  status: AssignmentApprovalStatus;
  /** 왜 승인이 필요한지 — 승인 방식, 겹친 최고관리자 편성 이름 등. */
  reason?: string;
  /** 무엇을 요청했는가. 비어 있으면 예전 데이터(새 편성 요청)로 본다. */
  action?: AssignmentAction;
  /** 수정·삭제 요청이 대체(또는 삭제)할 원본 편성. 승인되기 전까지 원본은 그대로 재생된다. */
  replacesId?: string;
  requestedBy?: string | null;
  requestedAt?: string;
  decidedBy?: string | null;
  decidedAt?: string;
  /** 반려 사유 등 처리자가 남긴 말. */
  note?: string;
};

/** 승인 대기·반려·요청 취소된 편성은 재생하지 않는다. 승인 전까지는 원본(있다면)이 그대로 재생된다. */
export const isAssignmentPlayable = (assignment: Pick<ScheduleAssignment, 'approval'>) =>
  assignment.approval?.status !== 'rejected' && assignment.approval?.status !== 'pending'
  && assignment.approval?.status !== 'cancelled';

/** 승인 요청(대기·반려·취소)으로 존재하는 행. 원본이 따로 있거나(replacesId) 아직 적용되지 않은 새 편성이다. */
export const isAssignmentProposal = (assignment: Pick<ScheduleAssignment, 'approval'>) =>
  assignment.approval?.status === 'pending' || assignment.approval?.status === 'rejected'
  || assignment.approval?.status === 'cancelled';

export const approvalLabel = (status: AssignmentApprovalStatus) =>
  status === 'pending' ? '승인 대기' : status === 'rejected' ? '반려됨' : status === 'cancelled' ? '요청 취소됨' : '승인됨';
export const approvalActionLabel = (action: AssignmentAction | undefined) =>
  action === 'update' ? '수정' : action === 'delete' ? '삭제' : '등록';

// ── 권한 서열 ──────────────────────────────────────────────────────────────
// 승인 방식과는 별개다. 상위 권한자가 올린 편성은 하위 권한자가 고치거나 지울 수 없고 보기만 한다.
// 같은 서열끼리는 서로 고칠 수 있다(관리자 ↔ 관리자, 편집자 ↔ 편집자).
export const ROLE_RANK: Record<AssignmentRole, number> = { Owner: 3, Admin: 2, Editor: 1, Viewer: 0 };
export const roleLabel = (role: AssignmentRole) =>
  role === 'Owner' ? '소유자' : role === 'Admin' ? '관리자' : role === 'Editor' ? '편집자' : '조회자';
/** 올린 사람의 서열. 역할 기록이 없는 예전 편성은 중앙 편성이면 소유자, 아니면 편집자로 본다. */
export function assignmentCreatorRole(assignment: Pick<ScheduleAssignment, 'central' | 'createdByRole'>): AssignmentRole {
  return assignment.createdByRole ?? (assignment.central ? 'Owner' : 'Editor');
}
export function canModifyAssignment(actor: { role: AssignmentRole | null }, assignment: Pick<ScheduleAssignment, 'central' | 'createdByRole'>): boolean {
  if (!actor.role) return false;
  return ROLE_RANK[actor.role] >= ROLE_RANK[assignmentCreatorRole(assignment)];
}

export const MAX_REPEAT_COUNT = 999;
export const MAX_SKIP_DATES = 400;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86400000;
const pad = (n: number) => String(n).padStart(2, '0');
// 날짜 계산은 UTC 자정 기준으로만 한다 — 시간대·서머타임이 하루 경계를 흔들지 못한다.
const at = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const addAssignmentDays = (date: string, days: number) => iso(at(date) + days * DAY_MS);
const dayDelta = (from: string, to: string) => Math.round((at(to) - at(from)) / DAY_MS);
const weekdayOf = (date: string) => new Date(at(date)).getUTCDay();

/** 같은 일자를 유지한 채 달을 더한다. 그 달에 없는 일자(2/30 등)는 건너뛴다. */
function addMonthsKeepingDay(date: string, months: number): string | null {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  const total = year * 12 + (month - 1) + months;
  const nextYear = Math.floor(total / 12);
  const nextMonth = total % 12 + 1;
  const daysInMonth = new Date(Date.UTC(nextYear, nextMonth, 0)).getUTCDate();
  return day > daysInMonth ? null : `${String(nextYear).padStart(4, '0')}-${pad(nextMonth)}-${pad(day)}`;
}

/** 그 달의 몇 번째 주 무슨 요일인지. 1일부터 7일씩 끊어 센다. */
export const nthWeekdayOfMonth = (date: string) => Math.floor((Number(date.slice(8, 10)) - 1) / 7) + 1;

/** year년 month월의 nth번째 weekday. 그 달에 없으면 null(다섯째 주가 없는 달). */
export function weekdayInMonth(year: number, month: number, weekday: number, nth: number): string | null {
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const day = 1 + (weekday - firstWeekday + 7) % 7 + (nth - 1) * 7;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day > daysInMonth ? null : `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;
}

export function isValidAssignmentDates(startDate: string, endDate: string): boolean {
  return ISO_DATE.test(startDate) && ISO_DATE.test(endDate) && startDate <= endDate;
}

export function isValidAssignmentRule(rule: AssignmentRule): boolean {
  if (!isValidAssignmentDates(rule.startDate, rule.endDate)) return false;
  // 음력 기준은 변환 가능한 시작일에서만 쓸 수 있다.
  if (isLunar(rule) && solarToLunar(rule.startDate) === null) return false;
  if (rule.repeat.kind === 'weekly' && !rule.repeat.weekdays.some(day => Number.isInteger(day) && day >= 0 && day <= 6)) return false;
  if (rule.skipDates && (rule.skipDates.length > MAX_SKIP_DATES || rule.skipDates.some(date => !ISO_DATE.test(date)))) return false;
  if (rule.repeatEnd.kind === 'count') return Number.isInteger(rule.repeatEnd.count) && rule.repeatEnd.count >= 1 && rule.repeatEnd.count <= MAX_REPEAT_COUNT;
  if (rule.repeatEnd.kind === 'until') return ISO_DATE.test(rule.repeatEnd.date) && rule.repeatEnd.date >= rule.startDate;
  return true;
}

// ── 음력 변환 (KASI 기반 테이블, 1000–2050년) ───────────────────────────────
export type LunarDate = { year: number; month: number; day: number; intercalation: boolean };

export function solarToLunar(date: string): LunarDate | null {
  if (!ISO_DATE.test(date)) return null;
  const calendar = new KoreanLunarCalendar();
  if (!calendar.setSolarDate(Number(date.slice(0, 4)), Number(date.slice(5, 7)), Number(date.slice(8, 10)))) return null;
  const lunar = calendar.getLunarCalendar();
  return { year: lunar.year, month: lunar.month, day: lunar.day, intercalation: lunar.intercalation === true };
}

export function lunarToSolar(lunar: LunarDate): string | null {
  const calendar = new KoreanLunarCalendar();
  if (!calendar.setLunarDate(lunar.year, lunar.month, lunar.day, lunar.intercalation)) return null;
  const solar = calendar.getSolarCalendar();
  return `${String(solar.year).padStart(4, '0')}-${pad(solar.month)}-${pad(solar.day)}`;
}

/** "음력 8월 15일" — 윤달이면 "음력 윤4월 1일". 변환 범위를 벗어나면 null. */
export function lunarDateLabel(date: string): string | null {
  const lunar = solarToLunar(date);
  return lunar === null ? null : `음력 ${lunar.intercalation ? '윤' : ''}${lunar.month}월 ${lunar.day}일`;
}

/** 그 음력 해에 실제로 있는 달 목록. 평달 뒤에 윤달이 붙는다. */
export function lunarMonthOptions(year: number): Array<{ month: number; intercalation: boolean }> {
  const months: Array<{ month: number; intercalation: boolean }> = [];
  for (let month = 1; month <= 12; month++) {
    months.push({ month, intercalation: false });
    if (lunarToSolar({ year, month, day: 1, intercalation: true }) !== null) months.push({ month, intercalation: true });
  }
  return months;
}

/** 그 음력 달에 실제로 있는 날짜(29일 또는 30일까지). */
export function lunarDayOptions(year: number, month: number, intercalation: boolean): number[] {
  const days: number[] = [];
  for (let day = 1; day <= 30; day++) if (lunarToSolar({ year, month, day, intercalation }) !== null) days.push(day);
  return days;
}

/** 첫 회를 제외한 반복이 없는 규칙인지. */
const repeats = (rule: AssignmentRule) => rule.repeat.kind !== 'none';
/** 날짜 기준이 음력인지. */
export const isLunar = (rule: Pick<AssignmentRule, 'calendar'>) => rule.calendar === 'lunar';

/** startDate 이후의 날짜가 회차의 시작일이 될 수 있는지. */
function isOccurrenceStart(rule: AssignmentRule, date: string): boolean {
  if (date < rule.startDate) return false;
  const lunarAnchor = isLunar(rule) ? solarToLunar(rule.startDate) : null;
  const lunarDate = lunarAnchor === null ? null : solarToLunar(date);
  switch (rule.repeat.kind) {
    case 'none': return date === rule.startDate;
    case 'weekly': return rule.repeat.weekdays.includes(weekdayOf(date));
    case 'monthly': return lunarAnchor !== null
      ? lunarDate !== null && lunarDate.day === lunarAnchor.day
      : date.slice(8) === rule.startDate.slice(8);
    case 'monthly-weekday': return weekdayOf(date) === weekdayOf(rule.startDate)
      && nthWeekdayOfMonth(date) === nthWeekdayOfMonth(rule.startDate);
    case 'yearly': return lunarAnchor !== null
      ? lunarDate !== null && lunarDate.month === lunarAnchor.month
        && lunarDate.day === lunarAnchor.day && lunarDate.intercalation === lunarAnchor.intercalation
      : date.slice(5) === rule.startDate.slice(5);
  }
}

/** 회차 시작일을 순서대로 돌려준다. 반복 종료(횟수·날짜)를 여기서 적용한다. */
export function* assignmentOccurrences(rule: AssignmentRule): Generator<string> {
  const limit = rule.repeatEnd.kind === 'count' ? rule.repeatEnd.count : Number.POSITIVE_INFINITY;
  const until = rule.repeatEnd.kind === 'until' ? rule.repeatEnd.date : null;
  let produced = 0;
  const emit = (date: string | null): boolean => {
    if (date === null) return true;
    if (until !== null && date > until) return false;
    if (produced >= limit) return false;
    produced++;
    return true;
  };
  if (!repeats(rule)) { if (emit(rule.startDate)) yield rule.startDate; return; }
  if (rule.repeat.kind === 'weekly') {
    const first = weekdayOf(rule.startDate);
    const offsets = [...new Set(rule.repeat.weekdays)].map(day => (day - first + 7) % 7).sort((a, b) => a - b);
    if (!offsets.length) return;
    for (let index = 0; produced < limit; index++) {
      const date = addAssignmentDays(rule.startDate, Math.floor(index / offsets.length) * 7 + offsets[index % offsets.length]!);
      if (!emit(date)) return;
      yield date;
    }
    return;
  }
  if (isLunar(rule) && (rule.repeat.kind === 'yearly' || rule.repeat.kind === 'monthly')) {
    const anchor = solarToLunar(rule.startDate);
    if (anchor === null) return;
    // 그 해·그 달에 없는 음력 날짜(윤달·30일)는 건너뛴다. 회차로도 세지 않는다.
    for (let year = anchor.year; produced < limit && year <= anchor.year + 200; year++) {
      if (rule.repeat.kind === 'yearly') {
        const date = lunarToSolar({ ...anchor, year });
        if (date === null || date < rule.startDate) continue;
        if (!emit(date)) return;
        yield date;
        continue;
      }
      for (let month = 1; month <= 12 && produced < limit; month++) {
        // 평달 다음에 윤달이 온다. 윤달이 없는 달은 변환이 실패하므로 자연히 건너뛴다.
        for (const intercalation of [false, true]) {
          const date = lunarToSolar({ year, month, day: anchor.day, intercalation });
          if (date === null || date < rule.startDate) continue;
          if (!emit(date)) return;
          yield date;
        }
      }
    }
    return;
  }
  if (rule.repeat.kind === 'monthly-weekday') {
    const weekday = weekdayOf(rule.startDate);
    const nth = nthWeekdayOfMonth(rule.startDate);
    const year = Number(rule.startDate.slice(0, 4));
    const month = Number(rule.startDate.slice(5, 7));
    // 다섯째 주가 없는 달은 건너뛴다. 회차로도 세지 않는다.
    for (let offset = 0; produced < limit && offset <= 12 * 200; offset++) {
      const total = year * 12 + (month - 1) + offset;
      const date = weekdayInMonth(Math.floor(total / 12), total % 12 + 1, weekday, nth);
      if (date === null || date < rule.startDate) continue;
      if (!emit(date)) return;
      yield date;
    }
    return;
  }
  const step = rule.repeat.kind === 'monthly' ? 1 : 12;
  // 건너뛴 달은 회차로 세지 않는다. 무한 반복이어도 탐색은 유한하게 끊는다.
  for (let months = 0; produced < limit && months <= 12 * 200; months += step) {
    const date = addMonthsKeepingDay(rule.startDate, months);
    if (date === null) continue;
    if (!emit(date)) return;
    yield date;
  }
}

/** 이 날짜에 재생되는가. UI와 재생 엔진이 같은 함수를 쓴다. */
export function assignmentOccursOn(rule: AssignmentRule, date: string): boolean {
  if (!ISO_DATE.test(date) || !isValidAssignmentDates(rule.startDate, rule.endDate)) return false;
  if (date < rule.startDate) return false;
  // 건너뛴 날은 회차 자체는 유지한 채 그 하루만 빠진다.
  if (rule.skipDates?.includes(date)) return false;
  if (rule.repeat.kind === 'none') return date <= rule.endDate;
  const span = dayDelta(rule.startDate, rule.endDate);
  if (rule.repeatEnd.kind === 'count') {
    for (const start of assignmentOccurrences(rule)) {
      if (start > date) return false;
      if (dayDelta(start, date) <= span) return true;
    }
    return false;
  }
  const until = rule.repeatEnd.kind === 'until' ? rule.repeatEnd.date : null;
  for (let back = 0; back <= span; back++) {
    const start = addAssignmentDays(date, -back);
    if (start < rule.startDate) break;
    if (!isOccurrenceStart(rule, start)) continue;
    if (until !== null && start > until) continue;
    return true;
  }
  return false;
}

/** 마지막으로 재생되는 날. 끝이 없으면 null. */
export function assignmentLastDate(rule: AssignmentRule): string | null {
  if (!isValidAssignmentDates(rule.startDate, rule.endDate)) return null;
  const span = dayDelta(rule.startDate, rule.endDate);
  if (!repeats(rule)) return rule.endDate;
  if (rule.repeatEnd.kind === 'never') return null;
  let last: string | null = null;
  for (const start of assignmentOccurrences(rule)) last = start;
  return last === null ? null : addAssignmentDays(last, span);
}

/**
 * 건너뛴 날짜를 빼고도 재생될 날이 하루라도 남아 있는가.
 * 한 기기에서 모든 회차를 "이 날짜만 삭제"로 지우면 보이지 않는 편성 행만 남는다 — 저장 쪽이 이 판정으로 그 행을 지운다.
 * skipDates는 유한하므로 그 개수보다 하나 많은 회차만 보면 답이 정해진다(끝없는 반복도 유한하게 끊는다).
 */
export function assignmentHasRemainingDays(rule: AssignmentRule): boolean {
  if (!isValidAssignmentRule(rule)) return false;
  const skip = new Set(rule.skipDates ?? []);
  const span = dayDelta(rule.startDate, rule.endDate);
  let seen = 0;
  for (const start of assignmentOccurrences(rule)) {
    for (let back = 0; back <= span; back++) if (!skip.has(addAssignmentDays(start, back))) return true;
    if (++seen > skip.size) return false;
  }
  return false;
}

/** 사람이 읽는 반복 설명. */
export function describeAssignmentRule(rule: AssignmentRule): string {
  const labels = ['일', '월', '화', '수', '목', '금', '토'];
  const days = dayDelta(rule.startDate, rule.endDate) + 1;
  const weekday = (date: string) => labels[weekdayOf(date)];
  // 1회분이 며칠인지가 헷갈리지 않도록 날짜를 그대로 적는다.
  const period = rule.startDate === rule.endDate
    ? `${rule.startDate}(${weekday(rule.startDate)})`
      : `${rule.startDate}(${weekday(rule.startDate)}) ~ ${rule.endDate}(${weekday(rule.endDate)}) ${days}일`;
  const lunar = isLunar(rule) ? solarToLunar(rule.startDate) : null;
  const lunarDay = lunar === null ? '' : `음력 ${lunar.intercalation ? '윤' : ''}`;
  const repeat = rule.repeat.kind === 'none' ? '반복 없음'
    : rule.repeat.kind === 'weekly' ? weeklyLabel(rule)
      : rule.repeat.kind === 'monthly' ? lunar
        ? `매월 음력 ${lunar.day}일 반복` : `매월 ${Number(rule.startDate.slice(8))}일 반복`
        : rule.repeat.kind === 'monthly-weekday' ? `매월 ${monthlyWeekdayLabel(rule.startDate)} 반복`
          : lunar ? `매년 ${lunarDay}${lunar.month}월 ${lunar.day}일 반복`
            : `매년 ${Number(rule.startDate.slice(5, 7))}월 ${Number(rule.startDate.slice(8))}일 반복`;
  const end = !repeats(rule) ? ''
    : rule.repeatEnd.kind === 'count' ? ` · ${rule.repeatEnd.count}회까지`
      : rule.repeatEnd.kind === 'until' ? ` · ${rule.repeatEnd.date}까지` : ' · 종료 없음';
  return `${period} · ${repeat}${end}`;
}

const NTH_LABELS = ['첫째', '둘째', '셋째', '넷째', '다섯째'];
/** 매주 반복이 실제로 어떤 요일에 걸리는지 — 기간이 곧 요일이다. */
function weeklyLabel(rule: AssignmentRule): string {
  const labels = ['일', '월', '화', '수', '목', '금', '토'];
  const days = rule.repeat.kind === 'weekly' ? [...rule.repeat.weekdays].sort() : [];
  return days.length ? `매주 ${days.map(day => labels[day]).join('·')}요일 반복` : '매주 반복';
}
/** "둘째 수요일" — 매월 요일 기준 반복을 읽기 쉽게. */
export function monthlyWeekdayLabel(date: string): string {
  const labels = ['일', '월', '화', '수', '목', '금', '토'];
  return `${NTH_LABELS[Math.min(nthWeekdayOfMonth(date) - 1, NTH_LABELS.length - 1)]} ${labels[weekdayOf(date)]}요일`;
}

/**
 * 런타임 투영 — 편성을 기존 재생 엔진이 이해하는 Schedule 창으로 바꾼다.
 * 날짜 판정은 assignmentRule을 통해 selectScheduleCandidates가 직접 수행한다.
 * 템플릿 원본을 수정해도 이미 배정된 기기는 재배정 전까지 그대로다(스냅샷).
 */
export function projectAssignments(assignments: readonly ScheduleAssignment[], schedules: readonly Schedule[]): Schedule[] {
  const derived: Schedule[] = [];
  for (const assignment of assignments) {
    if (!isValidAssignmentRule(assignment)) continue;
    // 반려는 제외하고, 대기는 하위 우선순위 메타데이터를 함께 투영한다.
    if (!isAssignmentPlayable(assignment)) continue;
    const rule: AssignmentRule = { startDate: assignment.startDate, endDate: assignment.endDate,
      repeat: assignment.repeat, repeatEnd: assignment.repeatEnd,
      ...(assignment.calendar === 'lunar' ? { calendar: 'lunar' as const } : {}),
      ...(assignment.skipDates?.length ? { skipDates: assignment.skipDates } : {}) };
    for (const block of assignment.blocks) {
      if (block.startMinute >= block.endMinute) continue;
      derived.push({
        id: `assignment-${assignment.id}-${block.id}`,
        name: block.label || assignment.name,
        groupId: assignment.groupId,
        enabled: true,
        published: true,
        layer: assignment.layer,
        assignmentRule: rule,
        assignmentPending: assignment.approval?.status === 'pending',
        target: { kind: 'playlist', playlistId: block.playlistId },
        targetDeviceIds: [assignment.deviceId],
        scheduleTypeId: null,
        recurrence: {
          kind: 'time-of-day',
          hour: Math.floor(block.startMinute / 60), minute: block.startMinute % 60,
          endHour: Math.floor(block.endMinute / 60), endMinute: block.endMinute % 60,
          weekdays: [],
        },
        validFrom: assignment.startDate,
        validUntil: assignmentLastDate(rule),
        priority: 0, repeatCount: 1, transitionType: 'none', transitionDurationMs: 0,
        reviewNote: null, createdAt: assignmentPriorityDate(assignment), updatedAt: assignment.updatedAt,
      });
    }
  }
  return [...schedules, ...derived];
}

// ── 겹침 판정 ──────────────────────────────────────────────────────────────
// 기기 한 대가 같은 시각에 틀 수 있는 화면은 하나다. 같은 레이어에서 편성이 겹치면
// 재생 엔진은 compareSchedulePriority에 따라 "나중에 만든 편성"을 고른다.
// 아래 두 함수는 그 규칙을 화면에서 그대로 설명하기 위한 것이다.

/** 재생에서 이기는 순서대로 정렬한다. 나중에 만든 편성이 앞에 온다. */
export function assignmentPriorityDate(assignment: Pick<ScheduleAssignment, 'createdAt' | 'approval' | 'priorityAt'>): string {
  if (assignment.priorityAt) return assignment.priorityAt;
  return assignment.approval?.status === 'approved' ? assignment.approval.decidedAt ?? assignment.createdAt : assignment.createdAt;
}

/**
 * 어떤 편성을 target 바로 위(또는 아래)에 놓기 위한 순서 값.
 * 이웃한 두 편성 사이를 잡아, 다른 편성의 값은 건드리지 않고 자기 값만 옮긴다.
 */
export function priorityBetween(target: string, neighbour: string | null, above: boolean): string {
  const edge = Date.parse(target);
  if (neighbour === null) return new Date(edge + (above ? 1000 : -1000)).toISOString();
  const other = Date.parse(neighbour);
  const middle = Math.floor((edge + other) / 2);
  return new Date(middle === edge ? edge + (above ? 1 : -1) : middle).toISOString();
}

/** 재생 순서대로 줄 세운 뒤, 어떤 편성을 target 위/아래로 옮길 값을 계산한다. */
export function reorderedPriority<T extends Pick<ScheduleAssignment, 'createdAt' | 'id' | 'approval' | 'priorityAt'>>(
  siblings: readonly T[], targetId: string, above: boolean): string | null {
  const ordered = byAssignmentPrecedence(siblings);
  const index = ordered.findIndex(item => item.id === targetId);
  if (index < 0) return null;
  const neighbour = ordered[above ? index - 1 : index + 1];
  return priorityBetween(assignmentPriorityDate(ordered[index]!), neighbour ? assignmentPriorityDate(neighbour) : null, above);
}

export function byAssignmentPrecedence<T extends Pick<ScheduleAssignment, 'createdAt' | 'id' | 'approval'>>(assignments: readonly T[]): T[] {
  // 재생되는 편성 → 승인 대기(하위 우선순위로만 재생) → 반려(재생 안 됨) 순.
  // 반려를 날짜 순서에 섞어 두면 캘린더에서 재생 중인 편성보다 위에 쌓인다.
  const rank = (assignment: T) => assignment.approval?.status === 'rejected' || assignment.approval?.status === 'cancelled' ? 2
    : assignment.approval?.status === 'pending' ? 1 : 0;
  return assignments.slice().sort((a, b) => rank(a) - rank(b)
    || assignmentPriorityDate(b).localeCompare(assignmentPriorityDate(a)) || a.id.localeCompare(b.id));
}

/** 앞으로 lookaheadDays 안에서 두 규칙이 함께 재생되는 날짜. 무한 반복도 유한하게 본다. */
export function overlappingDates(a: AssignmentRule, b: AssignmentRule, lookaheadDays = 365): string[] {
  if (!isValidAssignmentRule(a) || !isValidAssignmentRule(b)) return [];
  const from = a.startDate > b.startDate ? a.startDate : b.startDate;
  const ends = [assignmentLastDate(a), assignmentLastDate(b)].filter((date): date is string => date !== null);
  const horizon = addAssignmentDays(from, lookaheadDays);
  const until = ends.length === 2 ? (ends[0]! < ends[1]! ? ends[0]! : ends[1]!) : ends[0] ?? horizon;
  const dates: string[] = [];
  for (let date = from; date <= until && date <= horizon; date = addAssignmentDays(date, 1)) {
    if (assignmentOccursOn(a, date) && assignmentOccursOn(b, date)) dates.push(date);
  }
  return dates;
}
